import { buttonVariants, cn, EmptyState, Eyebrow, PageHeader } from '@dnx/ui';
import { useQuery, useSuspenseQuery } from '@tanstack/react-query';
import { Link } from '@tanstack/react-router';
import { ArrowRight, ArrowUpRight, Check, ExternalLink } from 'lucide-react';
import type { ReactNode } from 'react';
import { calendarQuery, hhmm, TZ } from '../agenda/queries.js';
import { boardQuery } from '../crm/queries.js';
import { countsQuery } from '../inbox/queries.js';
import { greeting, todayLong } from '../lib/labels.js';
import { baseHost, portalUrl } from '../lib/host.js';
import { meQuery } from '../lib/session.js';

const SETUP_STEPS = [
  { label: 'Clínica cadastrada na plataforma', done: true },
  { label: 'Verificação em duas etapas da equipe', done: true },
  { label: 'Marca da clínica (logo e cores)', done: false },
  { label: 'WhatsApp conectado', done: false },
  { label: 'Equipe convidada', done: false },
  { label: 'Base de conhecimento aprovada pelo responsável técnico', done: false },
];

/** Indicador grande e clicável: número em destaque, rótulo curto, uma linha de contexto. */
function Stat({ to, label, value, hint, tone }: { to: string; label: string; value: ReactNode; hint: ReactNode; tone?: 'warning' }) {
  return (
    <Link to={to} className="group relative flex flex-col rounded-2xl border border-border bg-surface/60 p-6 backdrop-blur-sm transition-colors hover:border-border-strong hover:bg-surface">
      <span className="flex items-center justify-between text-[11px] font-semibold tracking-[0.2em] text-fg-muted uppercase">
        {label}
        <ArrowUpRight className="size-4 text-fg-subtle transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5 group-hover:text-fg" aria-hidden />
      </span>
      <span className={cn('mt-5 text-[3.4rem] leading-none font-semibold tracking-[-0.04em] tabular-nums',
        tone === 'warning' ? 'text-warning' : 'text-fg')}>{value}</span>
      <span className="mt-3 truncate text-[13.5px] text-fg-muted">{hint}</span>
    </Link>
  );
}

export function HomePage() {
  const { data: me } = useSuspenseQuery(meQuery);
  const org = me.organizations.find((o) => o.organizationId === me.activeOrganizationId);
  const perms = new Set(me.permissions);
  const today = new Intl.DateTimeFormat('en-CA', { timeZone: TZ }).format(new Date());
  const { data: counts } = useQuery({ ...countsQuery, enabled: perms.has('inbox.conversation.read') });
  const { data: day } = useQuery({ ...calendarQuery(today), enabled: perms.has('scheduling.appointment.read') });
  const { data: board } = useQuery({ ...boardQuery({}), enabled: perms.has('crm.lead.read') });

  const appts = (day ?? []).filter((a) => a.kind !== 'block' && a.status !== 'cancelled');
  const next = appts.find((a) => new Date(a.endsAt).getTime() > Date.now());
  const openLeads = board?.columns.filter((c) => c.stage !== 'won' && c.stage !== 'lost').reduce((n, c) => n + c.total, 0);
  const newLeads = board?.columns.find((c) => c.stage === 'new')?.total ?? 0;
  const done = SETUP_STEPS.filter((s) => s.done).length;
  const portalHost = org ? `${org.slug}.${baseHost()}` : '';
  const dash = <span className="text-fg-subtle">—</span>;

  return (
    <div className="mx-auto max-w-6xl">
      <PageHeader size="lg" eyebrow={<span className="first-letter:uppercase">{todayLong()}</span>}
        title={<>{greeting()}, {me.user.name.split(' ')[0]}.</>}
        description={<>O que pede atenção agora na <strong className="font-semibold text-fg">{org?.tradeName}</strong> e o que falta para a clínica rodar inteira no sistema.</>} />

      <section aria-label="Hoje" className="mt-12 grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
        {perms.has('inbox.conversation.read') && (
          <Stat to="/conversas" label="Aguardando resposta" value={counts ? counts.waiting : dash}
            tone={counts?.waiting ? 'warning' : undefined}
            hint={counts?.waiting ? 'Pacientes esperando a equipe no WhatsApp' : 'Ninguém esperando. Bom trabalho.'} />
        )}
        {perms.has('scheduling.appointment.read') && (
          <Stat to="/agenda" label="Consultas hoje" value={day ? appts.length : dash}
            hint={next ? <>Próxima às <strong className="font-semibold text-fg">{hhmm(next.startsAt)}</strong> · {next.contact?.name ?? 'bloqueio'}</> : 'Nenhuma consulta pela frente hoje.'} />
        )}
        {perms.has('crm.lead.read') && (
          <Stat to="/leads" label="Leads em aberto" value={board ? openLeads : dash}
            hint={newLeads ? `${newLeads} ${newLeads === 1 ? 'novo esperando' : 'novos esperando'} o primeiro contato` : 'Nenhum lead novo sem contato.'} />
        )}
      </section>

      <div className="mt-12 grid gap-4 lg:grid-cols-[minmax(0,1.55fr)_minmax(0,1fr)]">
        <EmptyState
          title={done === SETUP_STEPS.length ? 'Implantação concluída' : `Implantação: ${done} de ${SETUP_STEPS.length} etapas`}
          actions={perms.has('ai.kb.read') && (
            <Link to="/base-de-conhecimento" className={cn(buttonVariants({ size: 'lg' }), 'rounded-xl')}>
              Revisar a base de conhecimento<ArrowRight className="size-4" />
            </Link>
          )}>
          <p>Cada etapa libera uma parte do atendimento automático. A clínica já pode trabalhar enquanto completa as demais.</p>
          <div className="mt-5 h-1 overflow-hidden rounded-full bg-surface-muted" role="progressbar"
            aria-valuemin={0} aria-valuemax={SETUP_STEPS.length} aria-valuenow={done}>
            <div className="h-full rounded-full bg-primary" style={{ width: `${(done / SETUP_STEPS.length) * 100}%` }} />
          </div>
          <ul className="mt-5 grid gap-x-6 gap-y-2.5 sm:grid-cols-2">
            {SETUP_STEPS.map((s) => (
              <li key={s.label} className="flex items-start gap-2.5 text-[14px]">
                <span className={cn('mt-0.5 grid size-[18px] shrink-0 place-items-center rounded-full border',
                  s.done ? 'border-primary bg-primary text-primary-fg' : 'border-border-strong')}
                  aria-label={s.done ? 'Concluída' : 'Pendente'}>
                  {s.done && <Check className="size-3" strokeWidth={3} />}
                </span>
                <span className={s.done ? 'text-fg-muted' : 'text-fg'}>{s.label}</span>
              </li>
            ))}
          </ul>
        </EmptyState>

        <div className="flex flex-col justify-between rounded-2xl border border-border bg-surface/60 p-7">
          <div>
            <Eyebrow>Área do paciente</Eyebrow>
            <p className="mt-4 text-[1.35rem] leading-snug font-semibold tracking-[-0.02em] text-fg">
              O portal da clínica, com a sua marca
            </p>
            <p className="mt-2 text-[14px] leading-relaxed text-fg-muted">
              O paciente entra com e-mail e app autenticador. Libere o acesso pela ficha do lead ou pela conversa.
            </p>
          </div>
          <a href={portalUrl(portalHost)} target="_blank" rel="noreferrer"
            className="mt-6 flex items-center justify-between gap-3 rounded-xl border border-border bg-bg/60 px-4 py-3 font-mono text-[13px] text-fg hover:border-border-strong">
            <span className="truncate">{portalHost}</span><ExternalLink className="size-4 shrink-0 text-fg-subtle" />
          </a>
        </div>
      </div>
    </div>
  );
}
